import type { Messages } from "../messages";
import { plural } from "../types";

export const zhHans: Messages = {
  common: {
    copy: "复制",
    copied: "已复制",
    optional: "可选",
    show: "展开",
    hide: "收起",
    loading: "加载中…",
    on: "开启",
    off: "关闭",
  },

  language: {
    label: "语言",
  },

  header: {
    encrypted: "端到端加密",
  },

  bytes: {
    b: "{size} B",
    kb: "{size} KB",
    mb: "{size} MB",
    gb: "{size} GB",
  },

  // 中文不区分单复数，两种形式相同。
  duration: {
    standalone: {
      hours: plural({ other: "{count} 小时" }),
      days: plural({ other: "{count} 天" }),
      weeks: plural({ other: "{count} 周" }),
    },
    inSentence: {
      hours: plural({ other: "{count} 小时" }),
      days: plural({ other: "{count} 天" }),
      weeks: plural({ other: "{count} 周" }),
    },
  },

  create: {
    mode: { text: "文本", file: "文件" },
    textPlaceholder: "粘贴密码、API 密钥或私钥…",

    generator: {
      open: "生成密码",
      close: "关闭生成器",
      length: "长度",
      lowercase: "小写字母 (a–z)",
      uppercase: "大写字母 (A–Z)",
      digits: "数字 (0–9)",
      symbols: "符号 (!@#…)",
      excludeAmbiguous: "避免易混淆字符 (0/O, 1/l/I)",
      needCharset: "请至少选择一种字符类型。",
      generate: "生成并填入",
      note: "在您的浏览器中生成，并插入到下方输入框的光标处；若未放置光标，则添加到末尾。",
    },

    dropzone: {
      title: "将文件拖放到此处",
      hint: "或{browse}——最大 {size}",
      browse: "浏览文件",
      note: "在离开此设备之前即已加密",
      remove: "移除",
    },

    encStatus: "已在您的浏览器中加密。密钥存在于链接之中，绝不会留在我们的服务器上。",

    expiry: {
      label: "有效期",
      custom: "自定义",
      unitHours: "小时",
      unitDays: "天",
      unitWeeks: "周",
    },

    oneTime: {
      title: "查看一次后销毁",
      description: "机密内容在被打开的瞬间即被抹除。再次访问将一无所获。",
    },

    allowedEmail: {
      label: "限定一个邮箱地址",
      hint: "每位打开链接的人都需要用一次性验证码验证邮箱。在此填写地址，就意味着只有该收件人能够通过。",
      placeholder: "only-this-person@example.com",
    },

    notifyEmail: {
      title: "被查看时通知我",
      description: "机密内容每次被打开时，都会发出一封已读回执，并注明打开它的地址。",
      label: "回执发送至",
      placeholder: "you@example.com",
    },

    developer: {
      title: "开发者选项",
      webhookUrlLabel: "Webhook 地址",
      webhookUrlHint: "我们会在创建时以及每次被查看时，以 POST 发送一个 JSON 事件。",
      webhookSecretLabel: "Webhook 签名密钥",
      webhookSecretPlaceholder: "至少 16 个字符",
      webhookSecretHint:
        "用于对负载签名（HMAC-SHA256，通过 {header} 请求头发送）。只要设置了 Webhook 地址，此项即为必填。",
    },

    policy: {
      open: "此链接将在 {expiry} 后失效。任何持有该链接的人在验证邮箱后都可以打开它。",
      openOneTime:
        "此链接将在 {expiry} 后失效，或在被打开的那一刻失效，以先发生者为准。任何持有该链接的人在验证邮箱后都可以打开它。",
      restricted: "此链接将在 {expiry} 后失效。只有 {email} 可以打开它。",
      restrictedOneTime:
        "此链接将在 {expiry} 后失效，或在被打开的那一刻失效，以先发生者为准。只有 {email} 可以打开它。",
    },

    submit: "创建机密链接",
    submitting: "加密中…",

    error: {
      needText: "请输入要分享的文本。",
      needFile: "请选择要分享的文件。",
      needNotifyEmail: "请填写接收已读回执的地址，或关闭该通知。",
      webhookSecretTooShort: "Webhook 签名密钥至少需要 16 个字符。",
      generic: "创建机密内容时出了点问题。",
    },

    result: {
      title: "您的链接已就绪",
      subtitle: "您可以用任何方式把它发出去。没有井号后面的那段片段，这个链接毫无用处。",
      linkCaption: "陶土色的那段是解密密钥——它从不会传到我们这里",
      expires: "失效时间",
      destroyAfterOneView: "查看一次后销毁",
      restrictedTo: "限定给",
      anyVerifiedEmail: "任何已验证的邮箱",
      createAnother: "再创建一个",
    },
  },

  retrieve: {
    invalidLink: {
      title: "链接无效",
      body: "此链接缺少解密密钥。请确认您复制了完整的网址——包括 {hash} 之后的全部内容。",
      note: "如果您重新加载了此页面，请重新打开原始链接：页面加载的瞬间密钥就会从地址栏中移除，因此它不会留在浏览历史、截图，或本站上报的任何内容里。",
    },

    notFound: {
      title: "未找到机密内容",
      body: "该机密内容已过期、已被查看过（如果它是一次性的），或者从未存在过。",
    },

    alreadyRetrieved: {
      title: "已被打开",
      body: "这是一份一次性机密内容，已经有人打开过它。它无法再次被读取——您不能，其他任何人也不能。",
      note: "如果打开它的人就是您，而下载没有完成，请在接下来的几分钟内重试该链接。在那之后，这份机密内容将永久消失，发送者需要重新分享一次。",
    },

    decrypting: "解密中…",

    fatalError: {
      title: "出了点问题",
      cannotReachServer: "无法连接到服务器。",
      cannotRequestCode: "无法请求验证码。",
      cannotVerifyCode: "无法验证该验证码。",
      decryptionFailed: "解密失败——链接可能已损坏或不完整。",
    },

    email: {
      eyebrow: "有机密内容在等待",
      title: "有人给您发送了内容",
      body: "验证您的邮箱以打开它。验证码在十分钟内有效。",
      fileLine: "文件：{filename}（{size}）。",
      label: "您的邮箱",
      placeholder: "you@example.com",
      submit: "发送验证码",
      submitting: "发送中…",
      restricted: "此机密内容仅限指定的邮箱地址打开，而这不是那个地址。",
      locked: "错误次数过多。请重新获取验证码。",
    },

    otp: {
      title: "请输入验证码",
      body: "我们已将一次性验证码发送至 {email}。",
      label: "一次性验证码",
      submit: "解锁机密内容",
      submitting: "验证中…",
      oneTimeNote: "打开即销毁。关闭标签页之前，请先复制您需要的内容。",
      resend: "重新发送验证码",
      resendIn: "{seconds} 秒后可重发",
      incorrect: "验证码错误。",
      incorrectWithAttempts: plural({ other: "验证码错误（还剩 {count} 次机会）。" }),
    },

    success: {
      textTitle: "机密内容",
      fileTitle: "您的文件",
      destroyedOnServer: "已在服务器上销毁",
      clickToReveal: "点击以显示",
      sendOneBack: "回传一份",
      fileDownloaded: "{filename} 已在您的浏览器中解密并下载。",
      destroyedNotice:
        "这份机密内容已在服务器上销毁。重新加载此页面将一无所见。已于 {time} 验证为 {email}。",
    },
  },

  reply: {
    title: "回传内容",
    intro: "内容将发送给 {email}，只有对方能够打开。你可以回传一次。",
    receiptTo: "阅读回执将发送至 {email}，也就是你刚刚验证的地址。",
    expired: {
      title: "回传时限已过",
      body: "回传只能在打开密件的页面上进行，而且仅限之后的短暂时间内。请让发送方重新分享一个链接。",
    },
  },

  auth: {
    signInRequired: "创建新链接需要先登录。",
    expired: {
      title: "登录状态已过期",
      body: "此页面已打开较长时间，因此已退出登录。请重新登录以创建链接。此前输入的内容不会保留。",
    },
    signIn: "登录",
    signedInAs: "已登录为 {email}",
    signOut: "退出登录",
  },

  apiError: {
    rateLimited: "请求过于频繁。请稍候再试。",
    tooLarge: "该文件过大，无法分享。",
    unauthenticated: "您的会话已过期。请重新登录以创建链接。",
    webhookHostNotPermitted: "该 Webhook 地址指向本服务器不会发送请求的目标。",
    webhooksDisabled: "此服务器已关闭 Webhook。",
    validationFailed: "上面的部分内容未被接受。请检查后重试。",
  },
};
